import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { getTodayEastern, getDaysAgoEastern } from '../../lib/dateHelpers'

var MEDAL = ['\uD83E\uDD47', '\uD83E\uDD48', '\uD83E\uDD49']

function matchStandard(pool_dim, job_type, standards) {
  if (!pool_dim || !standards) return null
  var clean = String(pool_dim).toLowerCase().replace(/\s+/g, '')
  // Exact match first, then ilike-style fuzzy
  var exact = standards.find(function(s) {
    return (!job_type || s.job_type === job_type) && String(s.pool_size).toLowerCase().replace(/\s+/g, '') === clean
  })
  if (exact) return exact
  var fuzzy = standards.find(function(s) {
    return (!job_type || s.job_type === job_type) && clean.indexOf(String(s.pool_size).toLowerCase().replace(/\s+/g, '')) !== -1
  })
  return fuzzy || null
}

function calcElapsedHrs(clockIn) {
  if (!clockIn) return 0
  return Math.max(0, (Date.now() - new Date(clockIn).getTime()) / 3600000)
}

export default function LeaderboardPage() {
  var { user, profile } = useAuth()
  var [entries, setEntries] = useState([])
  var [standards, setStandards] = useState([])
  var [workers, setWorkers] = useState([])
  var [attendance, setAttendance] = useState([])
  var [loading, setLoading] = useState(true)
  var [view, setView] = useState('season') // 'season' | 'week'

  var fetchAll = useCallback(async function() {
    setLoading(true)
    var [eRes, sRes, wRes, aRes] = await Promise.all([
      supabase.from('job_time_entries').select('user_id, job_id, clock_in, clock_out, hours_worked, jobs(job_type, pool_dimensions, estimated_hours, business_id, status)')
        .not('clock_out', 'is', null),
      supabase.from('pool_time_standards').select('*'),
      supabase.from('profiles').select('id, full_name, role, is_active, season_start').in('role', ['laborer', 'crew_leader', 'project_manager', 'owner', 'office_staff']).eq('is_active', true),
      supabase.from('daily_attendance').select('user_id, work_date, total_hours, clock_in, clock_out, status'),
    ])
    setEntries(eRes.data || [])
    setStandards(sRes.data || [])
    setWorkers(wRes.data || [])
    setAttendance(aRes.data || [])
    setLoading(false)
  }, [])

  useEffect(function() { fetchAll() }, [fetchAll])

  if (loading) return <div style={st.wrap}><div style={st.loading}>Loading leaderboard...</div></div>

  // Ranking window. Season = each worker's own season_start (same definition the
  // hours page uses for "TOTAL HOURS THIS SEASON"), week = trailing 7 days ET.
  var todayET = getTodayEastern()
  var weekStartET = getDaysAgoEastern(6)
  var seasonStartFor = {}
  workers.forEach(function(w) { seasonStartFor[w.id] = w.season_start || null })

  var inWindow = function(userId, workDate) {
    if (view === 'week') return workDate >= weekStartET && workDate <= todayET
    var ss = seasonStartFor[userId]
    return ss ? workDate >= ss : true
  }

  // Hours worked, from daily_attendance (the clock workers actually punch).
  var byUser = {}
  var ensureUser = function(uid) {
    if (!byUser[uid]) byUser[uid] = { hours: 0, days: {}, jobs: new Set(), ratios: [], beats: 0, over: 0 }
    return byUser[uid]
  }
  attendance.forEach(function(a) {
    if (!a.user_id || !a.work_date) return
    if (!seasonStartFor.hasOwnProperty(a.user_id)) return // inactive / non-crew
    if (!inWindow(a.user_id, a.work_date)) return
    var u = ensureUser(a.user_id)
    var hrs = parseFloat(a.total_hours) || 0
    if (a.status === 'clocked_in' && !a.clock_out && a.clock_in) hrs = calcElapsedHrs(a.clock_in)
    u.hours += hrs
    u.days[a.work_date] = true
  })

  // Pace vs pool time standards, when job-level entries exist. Secondary detail only.
  entries.forEach(function(e) {
    if (!e.user_id || !seasonStartFor.hasOwnProperty(e.user_id)) return
    var entryDate = e.clock_in ? String(e.clock_in).slice(0, 10) : null
    if (entryDate && !inWindow(e.user_id, entryDate)) return
    var u = ensureUser(e.user_id)
    if (e.job_id) u.jobs.add(e.job_id)
    var std = e.jobs ? matchStandard(e.jobs.pool_dimensions, e.jobs.job_type, standards) : null
    var expected = std ? parseFloat(std.expected_hours) : (e.jobs && e.jobs.estimated_hours ? parseFloat(e.jobs.estimated_hours) : null)
    if (expected && expected > 0 && e.hours_worked) {
      var ratio = parseFloat(e.hours_worked) / expected
      u.ratios.push(ratio)
      if (ratio <= 1.0) u.beats++
      else u.over++
    }
  })

  // Build ranked list
  var rows = Object.keys(byUser).map(function(uid) {
    var u = byUser[uid]
    var w = workers.find(function(x) { return x.id === uid })
    var avgRatio = u.ratios.length > 0 ? u.ratios.reduce(function(s, r) { return s + r }, 0) / u.ratios.length : null
    return {
      id: uid,
      name: w ? w.full_name : 'Unknown',
      role: w ? w.role : '',
      hours: u.hours,
      dayCount: Object.keys(u.days).length,
      jobCount: u.jobs.size,
      avgRatio: avgRatio,
      beats: u.beats,
      over: u.over,
      measured: u.ratios.length,
    }
  })

  // Rank: most hours first. Name breaks ties so the order is stable across renders.
  rows.sort(function(a, b) {
    if (b.hours !== a.hours) return b.hours - a.hours
    return String(a.name).localeCompare(String(b.name))
  })

  var myRow = rows.find(function(r) { return r.id === user?.id })
  var myRank = myRow ? rows.indexOf(myRow) + 1 : null
  var myPct = myRow && myRow.avgRatio ? Math.round(100 / myRow.avgRatio) : null

  var totalDays = rows.reduce(function(s, r) { return s + r.dayCount }, 0)
  var totalHours = rows.reduce(function(s, r) { return s + r.hours }, 0)

  return (
    <div style={st.wrap}>
      <div style={st.header}>
        <div style={st.title}>LEADERBOARD</div>
        <div style={st.subtitle}>{view === 'season' ? 'Season Hours — Most First' : 'This Week — Most First'}</div>
      </div>

      <div style={st.tabRow}>
        <button onClick={function() { setView('season') }} style={Object.assign({}, st.tab, view === 'season' ? st.tabActive : {})}>SEASON</button>
        <button onClick={function() { setView('week') }} style={Object.assign({}, st.tab, view === 'week' ? st.tabActive : {})}>THIS WEEK</button>
      </div>

      {/* My stats hero */}
      {myRow && (
        <div style={st.myCard}>
          <div style={st.myLeft}>
            <div style={st.myRankLabel}>YOUR RANK</div>
            <div style={st.myRank}>#{myRank}</div>
            <div style={st.myTotal}>of {rows.length}</div>
          </div>
          <div style={st.myRight}>
            <div style={st.myPctLabel}>{view === 'season' ? 'YOUR SEASON HOURS' : 'YOUR HOURS THIS WEEK'}</div>
            <div style={Object.assign({}, st.myPct, { color: '#b48c3c' })}>{myRow.hours.toFixed(1)}</div>
            <div style={st.myBeats}>
              <span style={{ color: 'rgba(255,255,255,0.45)' }}>{myRow.dayCount} {myRow.dayCount === 1 ? 'day' : 'days'} worked</span>
              {myPct != null && <span style={{ color: 'rgba(255,255,255,0.2)' }}>{'  \u2022  '}</span>}
              {myPct != null && <span style={{ color: myPct >= 100 ? '#4a8c6a' : myPct >= 80 ? '#b48c3c' : '#e05c5c' }}>{myPct}% vs target</span>}
            </div>
          </div>
        </div>
      )}

      {/* Totals */}
      <div style={st.totalsRow}>
        <div style={st.totalBox}>
          <div style={st.totalVal}>{totalHours.toFixed(0)}</div>
          <div style={st.totalLbl}>HOURS</div>
        </div>
        <div style={st.totalBox}>
          <div style={st.totalVal}>{totalDays}</div>
          <div style={st.totalLbl}>DAYS</div>
        </div>
        <div style={st.totalBox}>
          <div style={st.totalVal}>{rows.length}</div>
          <div style={st.totalLbl}>CREW</div>
        </div>
      </div>

      {/* Rankings */}
      <div style={st.section}>
        <div style={st.sectionTitle}>RANKINGS</div>
        {rows.length === 0 ? (
          <div style={st.empty}>No hours logged yet. Clock in to get on the board!</div>
        ) : rows.map(function(r, i) {
          var isMe = r.id === user?.id
          var medal = i < 3 ? MEDAL[i] : null
          var pct = r.avgRatio ? Math.round(100 / r.avgRatio) : null
          var badgeColor = i === 0 ? '#ffd84a' : i === 1 ? '#c0c0c0' : i === 2 ? '#cd7f32' : 'rgba(255,255,255,0.15)'
          return (
            <div key={r.id} style={Object.assign({}, st.row, isMe ? st.rowMe : {})}>
              <div style={st.rankSlot}>
                {medal ? <span style={st.medal}>{medal}</span> : <span style={Object.assign({}, st.rankNum, { color: badgeColor })}>{i + 1}</span>}
              </div>
              <div style={st.nameCol}>
                <div style={Object.assign({}, st.name, isMe ? { color: '#b48c3c' } : {})}>{r.name}{isMe ? ' (YOU)' : ''}</div>
                <div style={st.nameSub}>
                  {r.dayCount} {r.dayCount === 1 ? 'day' : 'days'}
                  {pct != null && <span style={{ color: pct >= 100 ? '#4a8c6a' : pct >= 80 ? '#b48c3c' : '#e05c5c' }}>{' \u2022 ' + pct + '% vs standard'}</span>}
                </div>
              </div>
              <div style={st.scoreCol}>
                <div style={Object.assign({}, st.score, isMe ? { color: '#b48c3c' } : { color: '#ffffff' })}>{r.hours.toFixed(1)}</div>
                <div style={st.scoreSub}>HRS</div>
              </div>
            </div>
          )
        })}
      </div>

      {profile?.role !== 'laborer' && (
        <div style={st.footer}>Ranked by hours clocked{view === 'season' ? ' since each worker’s season start' : ' over the last 7 days'}. Pace vs pool time standards is shown where job-level entries exist.</div>
      )}
    </div>
  )
}

var st = {
  wrap: { fontFamily: "'Barlow', sans-serif", maxWidth: '720px', paddingBottom: '40px' },
  loading: { padding: '60px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '14px' },
  header: { marginBottom: '12px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  subtitle: { fontSize: '13px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' },

  tabRow: { display: 'flex', gap: '4px', marginBottom: '20px' },
  tab: { flex: 1, padding: '10px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  tabActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },

  myCard: { display: 'flex', padding: '20px', backgroundColor: 'rgba(180,140,60,0.08)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '8px', marginBottom: '16px', gap: '16px', alignItems: 'center' },
  myLeft: { textAlign: 'center', minWidth: '100px', paddingRight: '16px', borderRight: '1px solid rgba(180,140,60,0.2)' },
  myRankLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.4)' },
  myRank: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '44px', fontWeight: 800, color: '#b48c3c', lineHeight: 1 },
  myTotal: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px' },
  myRight: { flex: 1 },
  myPctLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px' },
  myPct: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '32px', fontWeight: 800, lineHeight: 1 },
  myBeats: { fontSize: '12px', marginTop: '8px' },

  totalsRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '24px' },
  totalBox: { padding: '12px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', textAlign: 'center' },
  totalVal: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '22px', fontWeight: 700, color: '#ffffff' },
  totalLbl: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' },

  section: { marginBottom: '16px' },
  sectionTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  empty: { padding: '32px', textAlign: 'center', color: 'rgba(255,255,255,0.2)', fontSize: '13px' },

  row: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', marginBottom: '4px' },
  rowMe: { backgroundColor: 'rgba(180,140,60,0.1)', borderColor: 'rgba(180,140,60,0.3)' },
  rankSlot: { minWidth: '38px', textAlign: 'center' },
  medal: { fontSize: '22px' },
  rankNum: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '18px', fontWeight: 700 },
  nameCol: { flex: 1, minWidth: 0 },
  name: { fontSize: '15px', fontWeight: 600, color: '#ffffff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  nameSub: { fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' },
  scoreCol: { textAlign: 'right', minWidth: '72px' },
  score: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '20px', fontWeight: 700 },
  scoreSub: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', letterSpacing: '1px', color: 'rgba(255,255,255,0.3)' },
  noScore: { fontSize: '16px', color: 'rgba(255,255,255,0.15)' },

  footer: { marginTop: '20px', padding: '12px', fontSize: '11px', color: 'rgba(255,255,255,0.3)', textAlign: 'center', fontStyle: 'italic' },
}
